import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { 
  ArrowLeft, Plus, Edit, Trash2, Eye, EyeOff, Calendar, 
  Image, X, Upload, FileText, ExternalLink, Bold, Italic, 
  Underline, Strikethrough, List, ListOrdered, Link as LinkIcon,
  AlignLeft, AlignCenter, AlignRight, Heading1, Heading2, Heading3, Quote
} from "lucide-react";
import { toast } from "sonner";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import LinkExtension from "@tiptap/extension-link";
import ImageExtension from "@tiptap/extension-image";
import TextAlign from "@tiptap/extension-text-align";
import UnderlineExtension from "@tiptap/extension-underline";
import { getAuthHeaders } from "@/utils/auth";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Toolbar button component
const ToolbarButton = ({ onClick, active, disabled, children, title }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={`p-2 rounded hover:bg-muted transition-colors ${active ? 'bg-primary/20 text-primary' : 'text-muted-foreground'} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
  >
    {children}
  </button>
);

// Rich Text Editor Component
const RichTextEditor = ({ content, onChange }) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      UnderlineExtension,
      LinkExtension.configure({
        openOnClick: false,
        HTMLAttributes: { class: 'text-primary underline' }
      }),
      ImageExtension.configure({
        HTMLAttributes: { class: 'rounded-lg max-w-full' }
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph']
      })
    ],
    content: content,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose prose-invert max-w-none min-h-[300px] p-4 focus:outline-none prose-headings:font-heading prose-a:text-primary'
      }
    }
  });

  const setLink = useCallback(() => {
    if (!editor) return;
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL', previousUrl);
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  const addImage = useCallback(() => {
    if (!editor) return;
    const url = window.prompt('Image URL');
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  }, [editor]);

  if (!editor) return null;

  return (
    <div className="border border-border rounded-lg overflow-hidden bg-card">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1 p-2 border-b border-border bg-card/50">
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          active={editor.isActive('heading', { level: 1 })}
          title="Heading 1"
        >
          <Heading1 className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          active={editor.isActive('heading', { level: 2 })}
          title="Heading 2"
        >
          <Heading2 className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          active={editor.isActive('heading', { level: 3 })}
          title="Heading 3"
        >
          <Heading3 className="w-4 h-4" />
        </ToolbarButton>
        
        <div className="w-px h-6 bg-border mx-1" />
        
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive('bold')}
          title="Bold"
        >
          <Bold className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive('italic')}
          title="Italic"
        >
          <Italic className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          active={editor.isActive('underline')}
          title="Underline"
        >
          <Underline className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleStrike().run()}
          active={editor.isActive('strike')}
          title="Strikethrough"
        >
          <Strikethrough className="w-4 h-4" />
        </ToolbarButton>
        
        <div className="w-px h-6 bg-border mx-1" />
        
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          active={editor.isActive('bulletList')}
          title="Bullet List"
        >
          <List className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          active={editor.isActive('orderedList')}
          title="Numbered List"
        >
          <ListOrdered className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          active={editor.isActive('blockquote')}
          title="Quote"
        >
          <Quote className="w-4 h-4" />
        </ToolbarButton>
        
        <div className="w-px h-6 bg-border mx-1" />
        
        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          active={editor.isActive({ textAlign: 'left' })}
          title="Align Left"
        >
          <AlignLeft className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          active={editor.isActive({ textAlign: 'center' })}
          title="Align Center"
        >
          <AlignCenter className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          active={editor.isActive({ textAlign: 'right' })}
          title="Align Right"
        >
          <AlignRight className="w-4 h-4" />
        </ToolbarButton>
        
        <div className="w-px h-6 bg-border mx-1" />
        
        <ToolbarButton
          onClick={setLink}
          active={editor.isActive('link')}
          title="Add Link"
        >
          <LinkIcon className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={addImage}
          title="Add Image"
        >
          <Image className="w-4 h-4" />
        </ToolbarButton>
      </div>
      
      {/* Editor Content */}
      <EditorContent editor={editor} />
    </div>
  );
};

export default function AdminNews() {
  const navigate = useNavigate();
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Editor state
  const [showEditor, setShowEditor] = useState(false);
  const [editingStory, setEditingStory] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    slug: "",
    excerpt: "",
    content: "",
    featured_image: "",
    is_published: false,
    tags: ""
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    checkAdminAndLoad();
  }, []);

  const checkAdminAndLoad = async () => {
    try {
      const response = await fetch(`${API}/admin/check`, { headers: getAuthHeaders() });
      if (!response.ok) {
        navigate("/dashboard");
        return;
      }
      setIsAdmin(true);
      await fetchStories();
    } catch {
      navigate("/dashboard");
    }
  };

  const fetchStories = async () => {
    try {
      const response = await fetch(`${API}/admin/news`, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setStories(data.stories || []);
      }
    } catch (error) {
      console.error("Error fetching stories:", error);
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .substring(0, 100);
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;
    setFormData(prev => ({
      ...prev,
      title,
      slug: editingStory ? prev.slug : generateSlug(title)
    }));
  };

  const openNewStory = () => {
    setEditingStory(null);
    setFormData({
      title: "",
      slug: "",
      excerpt: "",
      content: "",
      featured_image: "",
      is_published: false,
      tags: ""
    });
    setShowEditor(true);
  };

  const openEditStory = async (storyId) => {
    try {
      const response = await fetch(`${API}/admin/news/${storyId}`, { headers: getAuthHeaders() });
      if (response.ok) {
        const story = await response.json();
        setEditingStory(story);
        setFormData({
          title: story.title,
          slug: story.slug,
          excerpt: story.excerpt || "",
          content: story.content,
          featured_image: story.featured_image || "",
          is_published: story.is_published,
          tags: (story.tags || []).join(", ")
        });
        setShowEditor(true);
      }
    } catch (error) {
      toast.error("Failed to load story");
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append("file", file);

    try {
      const response = await fetch(`${API}/admin/news/upload-image`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: formDataUpload
      });

      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({ ...prev, featured_image: data.image_url }));
        toast.success("Image uploaded");
      } else {
        toast.error("Failed to upload image");
      }
    } catch (error) {
      toast.error("Upload error");
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast.error("Title is required");
      return;
    }
    if (!formData.content.trim()) {
      toast.error("Content is required");
      return;
    }

    setSaving(true);

    const payload = {
      title: formData.title,
      slug: formData.slug || generateSlug(formData.title),
      excerpt: formData.excerpt || null,
      content: formData.content,
      featured_image: formData.featured_image || null,
      is_published: formData.is_published,
      tags: formData.tags ? formData.tags.split(",").map(t => t.trim()).filter(Boolean) : []
    };

    try {
      const url = editingStory 
        ? `${API}/admin/news/${editingStory.story_id}`
        : `${API}/admin/news`;
      
      const response = await fetch(url, {
        method: editingStory ? "PUT" : "POST",
        headers: getAuthHeaders(),
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success(editingStory ? "Story updated" : "Story created");
        setShowEditor(false);
        fetchStories();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to save story");
      }
    } catch (error) {
      toast.error("Save error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (storyId) => {
    if (!window.confirm("Are you sure you want to delete this story?")) return;

    try {
      const response = await fetch(`${API}/admin/news/${storyId}`, {
        method: "DELETE",
        headers: getAuthHeaders()
      });

      if (response.ok) {
        toast.success("Story deleted");
        fetchStories();
      } else {
        toast.error("Failed to delete story");
      }
    } catch (error) {
      toast.error("Delete error");
    }
  };

  const togglePublished = async (story) => {
    try {
      const response = await fetch(`${API}/admin/news/${story.story_id}`, {
        method: "PUT",
        headers: getAuthHeaders(),
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({ is_published: !story.is_published })
      });

      if (response.ok) {
        toast.success(story.is_published ? "Story unpublished" : "Story published");
        fetchStories();
      }
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  };

  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="flex items-center justify-between px-4 py-3 lg:px-8">
          <div className="flex items-center gap-4">
            <Link to="/admin">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Admin
              </Button>
            </Link>
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              <span className="font-heading text-lg uppercase tracking-wider">News Management</span>
            </div>
          </div>
          
          <Button className="btn-primary" onClick={openNewStory} data-testid="new-story-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Story
          </Button>
        </div>
      </header>

      <main className="p-4 lg:p-8 max-w-6xl mx-auto">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="border-border bg-card">
            <CardContent className="p-4">
              <p className="text-muted-foreground text-xs uppercase">Total Stories</p>
              <p className="text-2xl font-heading font-bold">{stories.length}</p>
            </CardContent>
          </Card>
          <Card className="border-border bg-card">
            <CardContent className="p-4">
              <p className="text-muted-foreground text-xs uppercase">Published</p>
              <p className="text-2xl font-heading font-bold text-green-400">
                {stories.filter(s => s.is_published).length}
              </p>
            </CardContent>
          </Card>
          <Card className="border-border bg-card">
            <CardContent className="p-4">
              <p className="text-muted-foreground text-xs uppercase">Drafts</p>
              <p className="text-2xl font-heading font-bold text-yellow-400">
                {stories.filter(s => !s.is_published).length}
              </p>
            </CardContent>
          </Card>
          <Card className="border-border bg-card">
            <CardContent className="p-4">
              <p className="text-muted-foreground text-xs uppercase">Total Views</p>
              <p className="text-2xl font-heading font-bold">
                {stories.reduce((sum, s) => sum + (s.views || 0), 0)}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Stories List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading stories...</p>
          </div>
        ) : stories.length === 0 ? (
          <Card className="border-border bg-card">
            <CardContent className="py-12 text-center">
              <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
              <h3 className="font-heading text-xl uppercase mb-2">No Stories Yet</h3>
              <p className="text-muted-foreground mb-6">Create your first news story</p>
              <Button className="btn-primary" onClick={openNewStory}>
                <Plus className="w-4 h-4 mr-2" /> Create Story
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {stories.map((story) => (
              <Card key={story.story_id} className="border-border bg-card" data-testid={`story-row-${story.story_id}`}>
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                    {story.featured_image ? (
                      <div className="w-20 h-20 rounded bg-black/20 flex-shrink-0 overflow-hidden">
                        <img 
                          src={story.featured_image.startsWith('/api') ? API_BASE + story.featured_image : story.featured_image}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="w-20 h-20 rounded bg-muted flex-shrink-0 flex items-center justify-center hidden sm:flex">
                        <Image className="w-8 h-8 text-muted-foreground" />
                      </div>
                    )}

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 className="font-heading text-lg font-bold truncate">{story.title}</h3>
                        {story.is_published ? (
                          <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Published</Badge>
                        ) : (
                          <Badge variant="outline" className="text-yellow-400 border-yellow-500/30">Draft</Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mb-2 truncate">/{story.slug}</p>
                      <div className="flex items-center gap-3 sm:gap-4 text-xs text-muted-foreground flex-wrap">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(story.created_at)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Eye className="w-3 h-3" />
                          {story.views || 0} views
                        </span>
                        <span className="hidden sm:inline">By {story.author_name}</span>
                      </div>
                    </div>

                    {/* Action Controls - Stack on mobile */}
                    <div className="flex items-center gap-1 border-t sm:border-t-0 pt-3 sm:pt-0 mt-1 sm:mt-0 justify-end sm:justify-start">
                      {story.is_published && (
                        <a href={`/news/${story.slug}`} target="_blank" rel="noopener noreferrer">
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" title="View Live">
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                        </a>
                      )}
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => togglePublished(story)}
                        className="h-8 w-8 p-0"
                        title={story.is_published ? "Unpublish" : "Publish"}
                        data-testid={`toggle-publish-${story.story_id}`}
                      >
                        {story.is_published ? (
                          <EyeOff className="w-4 h-4 text-yellow-400" />
                        ) : (
                          <Eye className="w-4 h-4 text-green-400" />
                        )}
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => openEditStory(story.story_id)}
                        className="h-8 w-8 p-0"
                        title="Edit"
                        data-testid={`edit-story-${story.story_id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleDelete(story.story_id)}
                        className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                        title="Delete"
                        data-testid={`delete-story-${story.story_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Editor Dialog */}
      <Dialog open={showEditor} onOpenChange={setShowEditor}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto bg-background border-border">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase">
              {editingStory ? "Edit Story" : "New Story"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={handleTitleChange}
                placeholder="Enter story title"
                className="bg-card"
                data-testid="story-title-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="slug">URL Slug</Label>
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground text-sm">/news/</span>
                <Input
                  id="slug"
                  value={formData.slug}
                  onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                  placeholder="url-friendly-slug"
                  className="bg-card flex-1"
                  data-testid="story-slug-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="excerpt">Excerpt</Label>
              <Textarea
                id="excerpt"
                value={formData.excerpt}
                onChange={(e) => setFormData(prev => ({ ...prev, excerpt: e.target.value }))}
                placeholder="Brief summary shown in listings..."
                className="bg-card"
                rows={2}
                data-testid="story-excerpt-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Featured Image</Label>
              <div className="flex items-start gap-4">
                {formData.featured_image ? (
                  <div className="relative w-32 h-24 rounded overflow-hidden bg-black/20">
                    <img 
                      src={formData.featured_image.startsWith('/api') ? API_BASE + formData.featured_image : formData.featured_image}
                      alt="Featured"
                      className="w-full h-full object-cover"
                    />
                    <Button
                      variant="destructive"
                      size="sm"
                      className="absolute top-1 right-1 h-6 w-6 p-0"
                      onClick={() => setFormData(prev => ({ ...prev, featured_image: "" }))}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                ) : (
                  <div className="w-32 h-24 rounded border-2 border-dashed border-border flex items-center justify-center">
                    <Image className="w-8 h-8 text-muted-foreground" />
                  </div>
                )}
                <div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    id="featured-image-upload"
                  />
                  <label htmlFor="featured-image-upload">
                    <Button variant="outline" size="sm" asChild disabled={uploading}>
                      <span>
                        <Upload className="w-4 h-4 mr-2" />
                        {uploading ? "Uploading..." : "Upload Image"}
                      </span>
                    </Button>
                  </label>
                  <p className="text-xs text-muted-foreground mt-2">Or paste image URL:</p>
                  <Input
                    value={formData.featured_image}
                    onChange={(e) => setFormData(prev => ({ ...prev, featured_image: e.target.value }))}
                    placeholder="https://..."
                    className="bg-card mt-1 text-sm"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="tags">Tags (comma separated)</Label>
              <Input
                id="tags"
                value={formData.tags}
                onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))}
                placeholder="news, update, feature"
                className="bg-card"
                data-testid="story-tags-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Content *</Label>
              <RichTextEditor 
                content={formData.content}
                onChange={(content) => setFormData(prev => ({ ...prev, content }))}
              />
            </div>

            <div className="flex items-center justify-between p-4 bg-card rounded border border-border">
              <div>
                <Label htmlFor="published" className="text-base">Publish Story</Label>
                <p className="text-sm text-muted-foreground">Make this story visible to the public</p>
              </div>
              <Switch
                id="published"
                checked={formData.is_published}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_published: checked }))}
                data-testid="story-published-toggle"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditor(false)}>
              Cancel
            </Button>
            <Button className="btn-primary" onClick={handleSave} disabled={saving} data-testid="save-story-btn">
              {saving ? "Saving..." : (editingStory ? "Update Story" : "Create Story")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
